import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { parseDate } from '@datepicker-react/hooks';
import moment from 'moment';

import { AirportSearchViewModel, WebUrlType } from '@api/v3';
import { PACKAGE_PAST_SEARCH } from '@constants/constants';
import {
  DateType,
  DEFAULT_DATE_FORMAT,
  FORM_DATE_FORMAT,
  formatMomentDate,
  getCurrentDate,
  isBeforeDateTime,
  URL_DATE_FORMAT,
} from '@utils/dateUtil';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import clean from '@utils/cleanObject';
import { handleFilterAndUpdateUrl } from '@utils/utils';
import { callService } from '@api/remote-call';
import useSearchBar from '@components/views/flight/hooks/useSearchBar';
import AirportSearch from '@components/views/package/detail/components/airport-searchNext';
import { PACKAGE_MAX_AGE } from '@components/views/package/detail/components/package-search-bar/SearchBarInterface';
import { package_ga4Event } from '@utils/ga4Event/package_ga4Event';
import {
  getSafeLocalStorageItem,
  removeSafeLocalStorageItem,
  setSafeLocalStorageItem,
} from '@utils/localStorage/getSafeLocalStorage';

import { BookerSearchDrawer } from '../common/BookerSearchDrawer';
import { BookerSearchViewModel } from '../common/BookerSearchViewModel';
import HotelBookerDatePicker from '../HotelBookerDatePicker';
import {
  generateSearchRooms,
  packageItemsTOStringify,
  validateGuestsChildrenAges,
} from '../common/bookerUtils';
import {
  AirportsContainer,
  LocationAnimationErrorBox,
} from './components/CyprusAirportSearch/styled';
import { SearchWrapper } from './styled';
import BookerGuestListingContainer from './components/booker-search-guests/BookerGuestListingContainer';

const placeholder = 'Otel Adı Veya Konum';

export interface PackageBookerError {
  location?: {
    departure?: boolean;
    hotel?: boolean;
  };
  date?: boolean;
}

interface Props {
  onHrefUpdate: (href: string) => void;
  onSearchCalled: () => void;
  href: string;
  type: string;
  setError: Dispatch<SetStateAction<Partial<PackageBookerError | undefined>>>;
  setInitialError: Dispatch<
    SetStateAction<Partial<PackageBookerError | undefined>>
  >;
  setGuestError: Dispatch<SetStateAction<boolean>>;
  error?: PackageBookerError;
  initialError?: PackageBookerError;
  isHomePage?: boolean;
  isHoliday?: boolean;
  setLocalRooms?: Dispatch<SetStateAction<ListingSearchRoomInterface[]>>;
  localRooms: ListingSearchRoomInterface[];
  setDeparturePackage: Dispatch<
    SetStateAction<AirportSearchViewModel | undefined>
  >;
}
const isDateOver = (date: Date) => {
  return isBeforeDateTime(
    moment(date),
    moment(
      parseDate(
        getCurrentDate().format(FORM_DATE_FORMAT),
        String(`dd/MM/yyyy`),
        new Date()
      )
    )
  );
};
export const CyprusTabContent = ({
  onHrefUpdate,
  onSearchCalled,
  href,
  type,
  error,
  setError,
  setInitialError,
  setGuestError,
  isHomePage,
  isHoliday,
  localRooms,
  setLocalRooms,
  setDeparturePackage,
}: Props) => {
  const [hrefProps, setHrefProps] = useState<{
    [key: string]: string | number | string[] | undefined;
  }>();
  const [pastPackageItems, setPastPackageItems] = useState<any[]>([]);
  const [selectedPreviousPackage, setSelectedPreviousPackage] = useState(false);

  const [hotelRooms, setHotelRooms] = useState(localRooms);
  const {
    departureAirportSearchText,
    departureAirport,
    onDepartureAirportSelect,
    onDepartureAirportTextChange,
  } = useSearchBar();

  const [location, setLocation] = useState<BookerSearchViewModel | null>(null);
  const [localStartDate, setLocalStartDate] = useState<DateType | null>(null);
  const [localEndDate, setLocalEndDate] = useState<DateType | null>(null);

  useEffect(() => {
    const pastItems = getSafeLocalStorageItem(PACKAGE_PAST_SEARCH)
      ? JSON.parse(getSafeLocalStorageItem(PACKAGE_PAST_SEARCH) || '[]')
      : [];

    const filteredItem = pastItems.filter(
      (item: any) =>
        !isDateOver(parseDate(item.in, String(`yyyy-MM-dd`), new Date()))
    );

    setSafeLocalStorageItem(
      PACKAGE_PAST_SEARCH,
      JSON.stringify(filteredItem, packageItemsTOStringify)
    );

    setPastPackageItems(filteredItem);
  }, []);
  const clearPastPackageSearch = () => {
    removeSafeLocalStorageItem(PACKAGE_PAST_SEARCH);
    setPastPackageItems([]);
  };
  const removeItemFromLocalStorage = (item: BookerSearchViewModel) => {
    const newItems = pastPackageItems?.filter(
      (i: BookerSearchViewModel) => i.text !== item.text
    );

    setPastPackageItems(newItems);
    setLocalStartDate(null);
    setLocalEndDate(null);
    setSafeLocalStorageItem(
      PACKAGE_PAST_SEARCH,
      JSON.stringify(newItems, packageItemsTOStringify)
    );
  };
  const setPreviousHotelRooms = (item: BookerSearchViewModel) => {
    const targetItem = pastPackageItems.find(
      (i: BookerSearchViewModel) => i.text === item.text
    );
    onDepartureAirportSelect(targetItem.departureAirport);
    setSelectedPreviousPackage(true);

    if (!targetItem) return;

    setHotelRooms(targetItem.rooms);
    setLocalRooms?.(targetItem.rooms);

    if (targetItem.in && targetItem.out) {
      setLocalStartDate(moment(targetItem.in));
      setLocalEndDate(moment(targetItem.out));
    }
    const roomUrlStr = generateSearchRooms(targetItem.rooms);

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          slug: targetItem?.url,
          in: targetItem.in || null,
          out: targetItem.out || null,
          room: roomUrlStr,
        })
      )
    );
  };
  // ===========================================
  // Handel date change
  // ===========================================
  const onDatesChange = (dates?: {
    startDate?: DateType | null;
    endDate?: DateType | null;
  }) => {
    const props = {
      ...hrefProps,
      in: formatMomentDate(dates?.startDate, URL_DATE_FORMAT),
      out: formatMomentDate(dates?.endDate, URL_DATE_FORMAT),
      isBooker: 'true',
    };
    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Handel on apply
  // ===========================================
  const onApplyGuest = (hr: ListingSearchRoomInterface[]) => {
    const roomUrlStr = generateSearchRooms(hr);
    const props = { ...hrefProps, room: roomUrlStr, isBooker: 'true' };

    setHrefProps(props);
    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  const onInputChange = (item: BookerSearchViewModel) => {
    const props = {
      ...hrefProps,
      slug: item?.url,
      isBooker: 'true',
    };
    setSelectedPreviousPackage(false);

    setHrefProps(props);
    setLocation(item);

    onHrefUpdate(handleFilterAndUpdateUrl(clean(props)));
  };

  // ===========================================
  // Keep the href props object up to date
  // ===========================================
  useEffect(() => {
    const roomUrlStr = generateSearchRooms(hotelRooms);
    setHrefProps({
      in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
      out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
      slug: location?.url,
      room: roomUrlStr,
      dep: departureAirport?.airportCode,
      depIsCity: departureAirport?.isCity ? 'true' : 'false',
      arr: 'ECN',
      isBooker: 'true',
      isPackage: 'true',
    });

    onHrefUpdate(
      handleFilterAndUpdateUrl(
        clean({
          in: formatMomentDate(localStartDate, URL_DATE_FORMAT),
          out: formatMomentDate(localEndDate, URL_DATE_FORMAT),
          slug: location?.url,
          room: roomUrlStr,
          dep: departureAirport?.airportCode,
          depIsCity: departureAirport?.isCity ? 'true' : 'false',
          arr: 'ECN',
          isBooker: 'true',
          isPackage: 'true',
        })
      )
    );
  }, [localStartDate, localEndDate, location, hotelRooms, departureAirport]);

  const hasSavedItems = useRef(false);

  useEffect(() => {
    const isRoomsError = validateGuestsChildrenAges(
      hotelRooms,
      PACKAGE_MAX_AGE
    );
    if (error?.location?.hotel || error?.location?.departure || error?.date)
      return;
    if (isRoomsError) return setGuestError(true);
    setGuestError(false);
    if (!href || !type) return;
    if (hasSavedItems.current) return;
    saveItems();

    hasSavedItems.current = true;
    const packageGa4EventData = {
      destination_point: location?.text || '',
      starting_point: departureAirportSearchText || '',
      arrival_date: formatMomentDate(localEndDate, DEFAULT_DATE_FORMAT),
      departure_date: formatMomentDate(localStartDate, DEFAULT_DATE_FORMAT),
      room_number: hotelRooms.length.toString(),
      adult_number: hotelRooms
        .reduce((acc, item) => acc + item.adultCount, 0)
        .toString(),
      child_number: hotelRooms
        .reduce((acc, item) => acc + item.childrenList.length, 0)
        .toString(),
    };
    package_ga4Event(packageGa4EventData);
  }, [onSearchCalled]);

  const saveItems = async () => {
    if (!href || !type) return;

    let pastPackageSearches = [];
    let packageItems: any[] = [];
    let newSearch: any = {};

    switch (type) {
      case WebUrlType.PACKAGE_CITY:
      case WebUrlType.PACKAGE_REGION:
      case WebUrlType.PACKAGE_THEME:
      case WebUrlType.HOTEL:
        if (getSafeLocalStorageItem(PACKAGE_PAST_SEARCH)) {
          pastPackageSearches = JSON.parse(
            getSafeLocalStorageItem(PACKAGE_PAST_SEARCH) || '[]'
          );
        }

        packageItems = pastPackageSearches ? [...pastPackageSearches] : [];

        if (location) {
          const data = await callService(
            {
              service: 'SearchesService',
              method: 'searchesPackage',
              version: 'v3',
            },
            {
              s: location.text as string,
            }
          );

          const targetLocation = data?.find(
            (item) => item.url === location.url
          );

          newSearch = {
            ...newSearch,
            ...targetLocation,
            slug: targetLocation?.url,
          };
        }
        if (localStartDate) {
          newSearch.in = formatMomentDate(localStartDate, URL_DATE_FORMAT);
        }
        if (localEndDate) {
          newSearch.out = formatMomentDate(localEndDate, URL_DATE_FORMAT);
        }
        newSearch.departureAirport = departureAirport;
        newSearch.rooms = hotelRooms;

        if (!packageItems.find((i) => i.url === location?.url)) {
          packageItems.unshift(newSearch);
        } else {
          const target = packageItems.find((i) => i.url === location?.url);

          const targetIndex = packageItems.indexOf(target);

          packageItems.splice(targetIndex, 1);
          packageItems.unshift(newSearch);
        }

        setSafeLocalStorageItem(
          PACKAGE_PAST_SEARCH,
          JSON.stringify(packageItems, packageItemsTOStringify)
        );
        window.open(href, '_self');
        break;

      default:
        window.open(href, '_self');
    }
  };

  useEffect(() => {
    if (!href.split('?')[0]) {
      setInitialError((val) => ({
        ...val,
        location: { ...val?.location, hotel: true },
      }));
    } else {
      setInitialError((val) => ({
        ...val,
        location: { ...val?.location, hotel: false },
      }));

      setError?.((val) => ({
        ...val,
        location: { ...val?.location, hotel: false },
      }));
    }
    if (!departureAirportSearchText) {
      if (!href?.includes('in') || !href?.includes('out')) {
        setInitialError?.((val) => ({
          ...val,
          location: { ...val?.location, departure: false },
        }));
      }
    } else {
      setInitialError?.((val) => ({
        ...val,
        location: { ...val?.location, departure: false },
      }));

      setError?.((val) => ({
        ...val,
        location: { ...val?.location, departure: false },
      }));

      if (!href?.includes('in') || !href?.includes('out')) {
        setInitialError?.((val) => ({
          ...val,
          date: true,
        }));
      }
    }
    if (!href?.includes('in') || !href?.includes('out')) {
      if (!departureAirportSearchText) {
        setInitialError?.((val) => ({
          ...val,
          date: false,
        }));
      }
    } else {
      setInitialError?.((val) => ({
        ...val,
        date: false,
      }));

      setError?.((val) => ({
        ...val,
        date: false,
      }));
      if (!departureAirportSearchText) {
        setInitialError?.((val) => ({
          ...val,
          location: { ...val?.location, departure: true },
        }));
      }
    }
  }, [
    href,
    location,
    departureAirportSearchText,
    localStartDate,
    localEndDate,
  ]);

  useEffect(() => {
    if (departureAirport) {
      setDeparturePackage(departureAirport);
    }
  }, [departureAirport]);

  const roomsToShow = useMemo(() => {
    return hotelRooms.length > 1 ? hotelRooms.slice(0, 1) : hotelRooms;
  }, [hotelRooms]);

  return (
    <SearchWrapper>
      <LocationAnimationErrorBox hasError={error?.location?.hotel ?? false}>
        <BookerSearchDrawer
          type='hotel'
          placeholder={placeholder}
          onInputLocationChange={onInputChange}
          responsiveTitle='Otel / Konum Arama'
          history={pastPackageItems?.slice(0, 3)}
          onClearClick={clearPastPackageSearch}
          onRemoveItem={removeItemFromLocalStorage}
          callBack={setPreviousHotelRooms}
          fetchItems={(param: string) => {
            return callService(
              {
                service: 'SearchesService',
                method: 'searchesPackage',
                version: 'v3',
              },
              {
                s: param || '',
              }
            );
          }}
        />
      </LocationAnimationErrorBox>

      <AirportsContainer>
        <LocationAnimationErrorBox
          hasError={error?.location?.departure ?? false}
        >
          <AirportSearch
            isHomePage={isHomePage}
            departureAirportSearchText={departureAirportSearchText}
            departureAirport={departureAirport}
            onDepartureAirportTextChange={onDepartureAirportTextChange}
            onDepartureAirportSelect={onDepartureAirportSelect}
            departureAirportError={error?.location?.departure}
          />
        </LocationAnimationErrorBox>
      </AirportsContainer>

      <LocationAnimationErrorBox hasError={error?.date ?? false}>
        <HotelBookerDatePicker
          dateChange={onDatesChange}
          setLocalEndDate={setLocalEndDate}
          setLocalStartDate={setLocalStartDate}
          isHoliday={isHoliday}
          localStartDate={localStartDate}
          localEndDate={localEndDate}
          isPreviousSearch={selectedPreviousPackage}
        />
      </LocationAnimationErrorBox>

      <BookerGuestListingContainer
        hotelRooms={roomsToShow}
        setHotelRooms={setHotelRooms}
        disableAddRoomButton={true}
        onApply={onApplyGuest}
        maxAdultCount={4}
        setLocalRooms={setLocalRooms}
        isBookerSearch
      />
    </SearchWrapper>
  );
};
export default CyprusTabContent;
